import { type FC } from 'react'
import { NavLink } from 'react-router-dom'
import { ApprovalMode, AutomaticInvitesMode, type AccountSchedulingSettings } from '../../../../models/Scheduling'
import { LabeledRadioCards, type RadioCardOption } from '../../../Scheduling/shared/ApprovalRulesEditor'
import { type UpdateAccountSettings } from '../SchedulingSetupTab'
import { SettingsCard, SettingsRow, SettingsSection, SettingsToggle } from './SettingsSection'

interface SectionProps {
  settings: AccountSchedulingSettings
  readOnly: boolean
  isSaving?: boolean
  onUpdate: UpdateAccountSettings
}

const bookingApprovalOptions: RadioCardOption<ApprovalMode>[] = [
  { value: ApprovalMode.ALWAYS_REQUIRE, label: 'Always require', description: 'All bookings wait for advisor approval.' },
  { value: ApprovalMode.NEVER_REQUIRE, label: 'Never require', description: 'All bookings confirm automatically.' },
  { value: ApprovalMode.USERS_DECIDE, label: 'Users decide', description: 'Each user sets their own rule.' },
]

const rescheduleApprovalOptions: RadioCardOption<ApprovalMode>[] = [
  { value: ApprovalMode.ALWAYS_REQUIRE, label: 'Always require', description: 'All reschedules wait for advisor approval.' },
  { value: ApprovalMode.NEVER_REQUIRE, label: 'Never require', description: 'All reschedules confirm automatically.' },
  { value: ApprovalMode.USERS_DECIDE, label: 'Users decide', description: 'Each user sets their own rule.' },
]

const automaticInviteOptions: RadioCardOption<AutomaticInvitesMode>[] = [
  { value: AutomaticInvitesMode.OFF, label: 'Off', description: "Invites don't send automatically." },
  { value: AutomaticInvitesMode.ON, label: 'On', description: 'Invites send automatically.' },
  { value: AutomaticInvitesMode.USERS_DECIDE, label: 'Users decide', description: 'Each user sets their own rule.' },
]

export const UserControlsSection: FC<SectionProps> = ({ settings, readOnly, isSaving = false, onUpdate }) => {
  return (
    <SettingsSection title='User controls' subtitle='Decide which scheduling settings each user can manage on their own.' isSaving={isSaving}>
      <h6 className='mb-0 zocks-text-gray-900'>Approval rules</h6>
      <SettingsCard>
        <SettingsRow>
          <LabeledRadioCards
            title='Booking approval'
            description='Set whether bookings need approval before confirmation emails go to clients.'
            name='Booking approval'
            options={bookingApprovalOptions}
            value={settings.approval?.booking ?? ApprovalMode.USERS_DECIDE}
            readOnly={readOnly}
            onChange={(booking) => onUpdate({ approval: { ...settings.approval, booking } })}
          />
        </SettingsRow>
        <SettingsRow>
          <LabeledRadioCards
            title='Reschedule approval'
            description='Set whether reschedule requests from clients need approval before the new time is confirmed.'
            name='Reschedule approval'
            options={rescheduleApprovalOptions}
            value={settings.approval?.reschedule ?? ApprovalMode.USERS_DECIDE}
            readOnly={readOnly}
            onChange={(reschedule) => onUpdate({ approval: { ...settings.approval, reschedule } })}
          />
        </SettingsRow>
      </SettingsCard>
      <h6 className='mb-0 zocks-text-gray-900'>Automations</h6>
      <SettingsCard>
        <SettingsRow>
          <LabeledRadioCards
            title='Automatic invites after meetings'
            description="Set whether Future Meetings sends a calendar invite automatically when a meeting has a set time and you're available according to your calendar. Meetings without an invite wait under Opportunities in Scheduling."
            name='Automatic invites after meetings'
            options={automaticInviteOptions}
            value={settings.automations?.automaticInvites ?? AutomaticInvitesMode.USERS_DECIDE}
            readOnly={readOnly}
            onChange={(automaticInvites) => onUpdate({ automations: { ...settings.automations, automaticInvites } })}
          />
        </SettingsRow>
      </SettingsCard>
      <h6 className='mb-0 zocks-text-gray-900'>Locations</h6>
      <SettingsCard>
        <SettingsRow>
          <SettingsToggle
            label='Allow users to manage their own locations'
            description='When off, users can pick account locations and defaults but cannot create or edit their own locations.'
            checked={settings.userControls?.allowManageOwnLocations ?? true}
            readOnly={readOnly}
            ariaLabel='Allow users to manage their own locations'
            onChange={(allowManageOwnLocations) => onUpdate({ userControls: { ...settings.userControls, allowManageOwnLocations } })}
          />
        </SettingsRow>
      </SettingsCard>
      <h6 className='mb-0 zocks-text-gray-900'>Collaboration</h6>
      <SettingsCard>
        <SettingsRow className='d-flex flex-column'>
          <span className='bold'>Share bookings between teammates</span>
          <span className='paragraph-small zocks-text-gray-600'>
            Sets the account default for sharing bookings between teammates. Manage it in <NavLink to='/account/manage-team'>Collaboration settings</NavLink>.
          </span>
        </SettingsRow>
      </SettingsCard>
    </SettingsSection>
  )
}
